import { useEffect, useRef } from "react";
import type { ReactNode } from "react";
import { useSearchParams } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ShieldCheck, Check, Info } from "../components/icons.js";
import { api, ApiError } from "../lib/api.js";
import { AuthBrand } from "./Login.js";

interface ApprovalView {
  name: string;
  email: string;
  orgName: string;
  plan: string;
  status: string;
  requestedAt: string;
  alreadyApproved?: boolean;
}

/** The Super Admin's emailed Approve button (0110) lands here: /approve-signup?token=…
 *  Opening the link only previews the signup (mail scanners prefetch links); the Approve button
 *  is what approves it. The token is single-use and bound to this one signup request. */
export function ApproveSignup(): ReactNode {
  const [params] = useSearchParams();
  const token = params.get("token") ?? "";

  const preview = useMutation({
    mutationFn: () => api.post<ApprovalView>("/signup-approval/preview", { token }),
  });
  const approve = useMutation({
    mutationFn: () => api.post<ApprovalView>("/signup-approval/approve", { token }),
  });

  const loadedFor = useRef<string | null>(null);
  useEffect(() => {
    if (!token || loadedFor.current === token) return;
    loadedFor.current = token;
    preview.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const shell = (title: string, body: ReactNode): ReactNode => (
    <div className="auth-wrap t1-auth">
      <AuthBrand />
      <div className="auth-card">
        <div className="auth-head">
          <span className="mark">
            <ShieldCheck />
          </span>
          <h1>{title}</h1>
        </div>
        {body}
      </div>
    </div>
  );

  const invalid = (msg = "This approval link is invalid or has expired. You can still approve the signup in the Owner console under Signup requests.") => (
    <div className="banner crit" style={{ marginBottom: 16 }}>
      <Info />
      <span>{msg}</span>
    </div>
  );

  if (!token) return shell("Approve signup", invalid());
  if (preview.isPending || preview.isIdle) return shell("Loading signup…", null);
  if (preview.isError) return shell("Approve signup", invalid());

  const data = approve.data ?? preview.data!;
  const details = (
    <dl className="t1-signup-summary">
      <div>
        <dt>Name</dt>
        <dd>{data.name}</dd>
      </div>
      <div>
        <dt>Email</dt>
        <dd>{data.email}</dd>
      </div>
      <div>
        <dt>Organisation</dt>
        <dd>{data.orgName}</dd>
      </div>
      <div>
        <dt>Plan</dt>
        <dd>{data.plan === "free" ? "Free" : "Paid"}</dd>
      </div>
      <div>
        <dt>Signed up</dt>
        <dd>{new Date(data.requestedAt).toLocaleString("en-IN")}</dd>
      </div>
    </dl>
  );

  if (data.status === "approved") {
    return shell(
      "Account approved",
      <>
        <div className="banner info" style={{ marginBottom: 16 }}>
          <Check />
          <span>
            {approve.data && !approve.data.alreadyApproved
              ? `${data.name} is approved and has been emailed a sign-in link.`
              : "This signup is already approved."}
          </span>
        </div>
        {details}
      </>,
    );
  }

  if (data.status !== "pending_approval") {
    return shell(
      "Approve signup",
      <>
        {invalid(data.status === "rejected" ? "This signup was declined, so it can't be approved from this link." : "This signup isn't awaiting approval.")}
        {details}
      </>,
    );
  }

  return shell(
    "Approve signup",
    <>
      <p className="auth-hint" style={{ marginTop: 0 }}>
        A new user has signed up. Review the details and approve the account.
      </p>
      {details}
      {approve.isError &&
        invalid(approve.error instanceof ApiError ? approve.error.message : "Couldn't approve. Please try again.")}
      <button
        className="btn primary"
        type="button"
        style={{ width: "100%", justifyContent: "center" }}
        disabled={approve.isPending}
        onClick={() => approve.mutate()}
      >
        {approve.isPending ? "Approving…" : "Approve"}
      </button>
    </>,
  );
}
